/**
 * Cliente para /api/psychologist/payment-info en psiplatform-be (proxy hacia
 * el backoffice de psima). No usa `beHttp`: su `parseJson` colapsa cualquier
 * body de error en un string único (ver beHttp.ts), y este endpoint necesita
 * preservar el detalle por campo de un 400 para resaltar los inputs del form
 * (ver PaymentDataFormModal). Reusa la misma auth (`getValidToken`) y la misma
 * base URL (`PSI_PLATFORM_BE_URL`) que beHttp.
 */

import { PSI_PLATFORM_BE_URL } from './config';
import { getValidToken, hasValidSession, redirectToLogin } from './auth';

const PAYMENT_INFO_PATH = '/api/psychologist/payment-info';

export type Currency = 'USD' | 'USDT' | 'ARS';
export type PaymentInfoStatus = 'complete' | 'incomplete';

export interface BankAddress {
  calle: string;
  ciudad: string;
  estado: string;
  codigoPostal: string;
  pais: string;
}

export interface AchPaymentData {
  currency: 'USD';
  plataforma: string;
  /** Tal cual figuran en la plataforma de cobro — psima los mapea a
   *  FIRST NAME / LAST NAME (y a ACCOUNT NAME combinados). */
  nombre: string;
  apellido: string;
  nombreBanco: string;
  routingNumber: string;
  numeroCuenta: string;
  tipoCuenta: 'checking' | 'savings';
  direccionBanco: BankAddress;
}

/** Fija, pero psima la exige igual en el body del POST — sin ella, 400. */
export const FIXED_USDT_NETWORK = 'Polygon (MATIC)';

export interface UsdtPaymentData {
  currency: 'USDT';
  red: string;
  plataformaOWallet: string;
  direccionWallet: string;
}

export interface ArsPaymentData {
  currency: 'ARS';
  lbTag: string;
  cuit: string;
}

export type PaymentData = AchPaymentData | UsdtPaymentData | ArsPaymentData;

export interface PaymentInfoResponse {
  exists: boolean;
  currency: Currency | null;
  status: PaymentInfoStatus | null;
  data: PaymentData | null;
}

export interface SavePaymentInfoSuccess {
  status: PaymentInfoStatus;
}

/** 400: errores de campo devueltos por psima, listos para mapear a los inputs. */
export class PaymentInfoFieldError extends Error {
  errors: Record<string, string>;
  constructor(errors: Record<string, string>) {
    super('Datos inválidos');
    this.errors = errors;
  }
}

/** Cualquier otro fallo (401/404/500/502/red): nunca se muestra el detalle real. */
export class PaymentInfoGenericError extends Error {}

async function requireToken(): Promise<string> {
  if (!hasValidSession()) {
    redirectToLogin();
    throw new PaymentInfoGenericError('Sesión expirada.');
  }
  const token = getValidToken();
  if (!token) {
    redirectToLogin();
    throw new PaymentInfoGenericError('Sesión expirada.');
  }
  return token;
}

async function safeJson(resp: Response): Promise<unknown> {
  const text = await resp.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export async function fetchPaymentInfo(): Promise<PaymentInfoResponse> {
  const token = await requireToken();

  let resp: Response;
  try {
    resp = await fetch(`${PSI_PLATFORM_BE_URL}${PAYMENT_INFO_PATH}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new PaymentInfoGenericError(
      'No pudimos cargar tus datos de cobro. Intentá de nuevo en unos minutos.',
    );
  }

  if (!resp.ok) {
    throw new PaymentInfoGenericError(
      'No pudimos cargar tus datos de cobro. Intentá de nuevo en unos minutos.',
    );
  }

  const payload = await safeJson(resp);
  return payload as PaymentInfoResponse;
}

export async function savePaymentInfo(
  data: PaymentData,
): Promise<SavePaymentInfoSuccess> {
  const token = await requireToken();

  let resp: Response;
  try {
    resp = await fetch(`${PSI_PLATFORM_BE_URL}${PAYMENT_INFO_PATH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
  } catch {
    throw new PaymentInfoGenericError(
      'No pudimos guardar tus datos de cobro. Intentá de nuevo en unos minutos.',
    );
  }

  if (resp.ok) {
    const payload = await safeJson(resp);
    return payload as SavePaymentInfoSuccess;
  }

  if (resp.status === 400) {
    const payload = await safeJson(resp);
    const rawErrors =
      payload && typeof payload === 'object' && 'errors' in payload
        ? ((payload as { errors?: unknown }).errors as Record<string, string> | undefined)
        : undefined;
    // psiplatform-be siempre manda `errors` (ver PaymentInfoService.extractFieldErrors),
    // pero si algún día llegara un 400 sin ese detalle, no lo tragamos en silencio: al
    // menos mostramos el `message` como error general en vez de cerrar sin feedback.
    const errors =
      rawErrors && Object.keys(rawErrors).length
        ? rawErrors
        : {
            _general:
              payload && typeof payload === 'object' && 'message' in payload && typeof (payload as { message?: unknown }).message === 'string'
                ? (payload as { message: string }).message
                : 'Revisá los datos ingresados.',
          };
    throw new PaymentInfoFieldError(errors);
  }

  // 401 / 404 / 500 / 502 / lo que sea: mismo error genérico, nunca se distingue
  // frente a la psicóloga (el detalle real ya se logueó server-side en psiplatform-be).
  throw new PaymentInfoGenericError(
    'No pudimos guardar tus datos de cobro. Intentá de nuevo en unos minutos.',
  );
}
